import React from 'react';
import {
  ATMContainer,
  ATMSegment,
  ATMHeader,
  ATMGrid,
  ATMTable,
  MOLReadonlyField,
} from 'shared-it-appmod-ui';
import { IFireDispatchDetails } from 'src/models/fire-fighting-dispatch.model';
import DispatchStatus from 'src/components/atoms/fire-dispatch-status/fire-dispatch-status.component';
import Moment from 'src/libraries/moment.library';
import Lang from 'src/libraries/language';
import styles from './fire-dispatch-details.module.scss';

interface IProps {
  fireDispatch: IFireDispatchDetails;
}

const FireDispatchDetails: React.FC<IProps> = ({ fireDispatch }) => {
  return (
    <ATMContainer>
      <ATMSegment>
        <ATMHeader>{Lang.TTL_FIRE_DISPATCH_FORM_INFORMATION}</ATMHeader>
        <ATMGrid stackable nonPadded columns={4} className={styles.details}>
          <ATMGrid.Row>
            <ATMGrid.Column>
              <MOLReadonlyField
                label={Lang.LBL_DISPATCH_DATE_AND_TIME}
                value={`${Moment(fireDispatch.dispatchDate).format(
                  'MM/DD/YYYY'
                )} - ${fireDispatch.dispatchTime} hrs`}
              />
            </ATMGrid.Column>
            <ATMGrid.Column>
              <MOLReadonlyField
                label={Lang.LBL_LIFTOFF_TIME}
                value={
                  fireDispatch.liftoffTime &&
                  fireDispatch.liftoffTime.length > 1 &&
                  `${fireDispatch.liftoffTime} hrs`
                }
              />
            </ATMGrid.Column>
            <ATMGrid.Column>
              <MOLReadonlyField
                label={Lang.LBL_FIRE_DISPATCH_RETURN_TIME}
                value={
                  fireDispatch.returnTime &&
                  fireDispatch.returnTime.length > 1 &&
                  `${fireDispatch.returnTime} hrs`
                }
              />
            </ATMGrid.Column>
            <ATMGrid.Column>
              <MOLReadonlyField
                label={Lang.LBL_FIRE_DISPATCH_RESPONSE_TIME}
                value={fireDispatch.responseTime}
              />
            </ATMGrid.Column>
          </ATMGrid.Row>
          <ATMGrid.Row>
            <ATMGrid.Column>
              <MOLReadonlyField
                label={Lang.LBL_FIRE_DISPATCH_FLIGHT_HOURS}
                value={fireDispatch.flightHours}
              />
            </ATMGrid.Column>
            <ATMGrid.Column>
              <MOLReadonlyField
                label={Lang.LBL_FIRE_DISPATCH_LOCATION}
                value={fireDispatch.location}
              />
            </ATMGrid.Column>
            <ATMGrid.Column width={8}>
              <MOLReadonlyField
                label={Lang.LBL_FIRE_DISPATCH_LAT_LONG}
                value={`${fireDispatch.lattitude} ${fireDispatch.longitude}`}
              />
            </ATMGrid.Column>
          </ATMGrid.Row>
        </ATMGrid>
      </ATMSegment>
      <ATMSegment>
        <ATMHeader>{Lang.TTL_FIRE_DISPATCH_FIRE_INFORMATION}</ATMHeader>
        <ATMGrid stackable nonPadded columns={2} className={styles.details}>
          <ATMGrid.Row>
            <ATMGrid.Column width={6}>
              <MOLReadonlyField
                label={Lang.LBL_FIRE_DISPATCH_FIRE_NAME}
                value={fireDispatch.fireName}
              />
            </ATMGrid.Column>
            <ATMGrid.Column width={6}>
              <MOLReadonlyField
                label={Lang.LBL_FIRE_DISPATCH_FIRE_AGENCY}
                value={fireDispatch.fireAgency}
              />
            </ATMGrid.Column>
          </ATMGrid.Row>
          <ATMGrid.Row>
            <ATMGrid.Column width={12}>
              <MOLReadonlyField
                label={Lang.LBL_FIRE_DISPATCH_FIRE_COMMENTS}
                value={fireDispatch.fireComments}
              />
            </ATMGrid.Column>
          </ATMGrid.Row>
        </ATMGrid>
      </ATMSegment>
      <ATMSegment>
        <ATMHeader>{Lang.TTL_FIRE_DISPATCH_FLIGHT_INFORMATION}</ATMHeader>
        <ATMGrid stackable nonPadded columns={4} className={styles.details}>
          <ATMGrid.Row>
            <ATMGrid.Column>
              <MOLReadonlyField
                label={Lang.LBL_FIRE_DISPATCH_HELICOPTER_COMPANY}
                value={fireDispatch.operatorName}
              />
            </ATMGrid.Column>
            <ATMGrid.Column>
              <MOLReadonlyField
                label={Lang.LBL_FIRE_DISPATCH_TAIL}
                value={fireDispatch.tailNumber}
              />
            </ATMGrid.Column>
            <ATMGrid.Column>
              <MOLReadonlyField
                label={Lang.LBL_FIRE_DISPATCH_PILOT}
                value={fireDispatch.pilot}
              />
            </ATMGrid.Column>
            <ATMGrid.Column>
              <MOLReadonlyField
                label={Lang.LBL_FIRE_DISPATCH_COPILOT}
                value={fireDispatch.coPilot}
              />
            </ATMGrid.Column>
          </ATMGrid.Row>
          <ATMGrid.Row>
            <ATMGrid.Column>
              <MOLReadonlyField
                label={Lang.LBL_FIRE_DISPATCH_FLIGHT_NUMBER}
                value={fireDispatch.flightNumber}
              />
            </ATMGrid.Column>
            <ATMGrid.Column>
              <MOLReadonlyField
                label={Lang.LBL_FIRE_DISPATCH_FUEL_CYCLES}
                value={fireDispatch.fuelCycles}
              />
            </ATMGrid.Column>
            <ATMGrid.Column>
              <MOLReadonlyField
                label={Lang.LBL_FIRE_DISPATCH_LANDING_CYCLES}
                value={fireDispatch.landingCycles}
              />
            </ATMGrid.Column>
            <ATMGrid.Column>
              <MOLReadonlyField
                label={Lang.LBL_FIRE_DISPATCH_BILLABLE_LAUNCH}
                value={fireDispatch.billableLaunch && fireDispatch.billableLaunch === 'true' ? 'Yes' : 'No'}
              />
            </ATMGrid.Column>
          </ATMGrid.Row>
        </ATMGrid>
        <ATMHeader as="h3">{Lang.TTL_REQUEST_FLIGHT_AIR_DROPS}</ATMHeader>
        <ATMTable>
          <ATMTable.Header>
            <ATMTable.Row>
              <ATMTable.HeaderCell>
                {fireDispatch.dipCount && fireDispatch.dipCount.length > 0
                  ? `${Lang.LBL_FIRE_DISPATCH_NUM_OF_DIPS} - ${fireDispatch.dipCount}`
                  : `${Lang.LBL_FIRE_DISPATCH_NUM_OF_DIPS} - n/a`}
              </ATMTable.HeaderCell>
              <ATMTable.HeaderCell>
                {fireDispatch.dropCount && fireDispatch.dropCount.length > 0
                  ? `${Lang.LBL_FIRE_DISPATCH_NUM_OF_DROPS} - ${fireDispatch.dropCount}`
                  : `${Lang.LBL_FIRE_DISPATCH_NUM_OF_DROPS} - n/a`}
              </ATMTable.HeaderCell>
              <ATMTable.HeaderCell>
                {fireDispatch.waterLiters && fireDispatch.waterLiters.length > 0
                  ? `${Lang.LBL_FIRE_DISPATCH_WATER_DROPPED} - ${fireDispatch.waterLiters} Liters`
                  : `${Lang.LBL_FIRE_DISPATCH_WATER_DROPPED} - n/a`}
              </ATMTable.HeaderCell>
              <ATMTable.HeaderCell>
                {fireDispatch.waterGallons &&
                fireDispatch.waterGallons.length > 0
                  ? `${Lang.LBL_FIRE_DISPATCH_WATER_DROPPED} - ${fireDispatch.waterGallons} Gallons`
                  : `${Lang.LBL_FIRE_DISPATCH_WATER_DROPPED} - n/a`}
              </ATMTable.HeaderCell>
            </ATMTable.Row>
          </ATMTable.Header>
          <ATMTable.Body>
            {fireDispatch.DispatchDrops &&
              fireDispatch.DispatchDrops.map((drops) => {
                return (
                  <ATMTable.Row key={drops.dispatchId}>
                    <ATMTable.Cell>{drops.dipCount}</ATMTable.Cell>
                    <ATMTable.Cell>{drops.dropCount}</ATMTable.Cell>
                    <ATMTable.Cell>{drops.waterLiters}</ATMTable.Cell>
                    <ATMTable.Cell>{drops.waterGallons}</ATMTable.Cell>
                  </ATMTable.Row>
                );
              })}
          </ATMTable.Body>
        </ATMTable>
      </ATMSegment>
      <ATMSegment>
        <ATMHeader>{Lang.TTL_FIRE_DISPATCH_FIRE_DISPATCH_STATUS}</ATMHeader>
        <ATMGrid stackable nonPadded columns={1} className={styles.details}>
          <ATMGrid.Row>
            <ATMGrid.Column>
              <MOLReadonlyField
                label={Lang.LBL_FIRE_DISPATCH_STATUS}
                value={
                  <DispatchStatus
                    dispatchStatus={fireDispatch.status ?? undefined}
                  />
                }
              />
            </ATMGrid.Column>
          </ATMGrid.Row>
          <ATMGrid.Row>
            <ATMGrid.Column>
              <MOLReadonlyField
                label={Lang.LBL_FIRE_DISPATCH_REASON_COMMENTS}
                value={fireDispatch.reason}
              />
            </ATMGrid.Column>
          </ATMGrid.Row>
        </ATMGrid>
      </ATMSegment>
    </ATMContainer>
  );
};

export default FireDispatchDetails;
